import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Modal } from 'bootstrap'
import { cable } from '../../cable.js'
import { t } from '../../i18n.js'
import { isoToDisplayDateOrDash as fmtDate } from '../utils/dateFormat.js'

const PROFILE_TYPE_VALUES = [ 'INDEXATION', 'GERMAN_HURDLE' ]

const fmtRate = (value) => {
  if (value === null || value === undefined || value === '') return '—'
  const n = Number(value)
  if (!Number.isFinite(n)) return '—'
  return `${n.toFixed(2)}%`
}

const newDraft = () => ({
  id: null,
  name: '',
  profile_type: 'INDEXATION',
  index_source_growth_rate_id: '',
  frequency_type: 'ANNUAL',
  first_review_date: '',
  min_rate: '',
  max_rate: '',
  hurdle_rate: ''
})

const profileToDraft = (profile) => ({
  id: profile.id,
  name: profile.name || '',
  profile_type: profile.profile_type || 'INDEXATION',
  index_source_growth_rate_id: profile.index_source_growth_rate_id ?? '',
  frequency_type: profile.frequency_type || 'ANNUAL',
  first_review_date: profile.first_review_date || '',
  min_rate: profile.min_rate ?? '',
  max_rate: profile.max_rate ?? '',
  hurdle_rate: profile.hurdle_rate ?? ''
})

const draftToPayload = (draft) => {
  const numeric = (value) => (value === '' || value === null ? null : Number(value))
  const stringOrNil = (value) => (value === '' || value === null ? null : value)

  const base = {
    name: draft.name,
    profile_type: draft.profile_type,
    frequency_type: 'ANNUAL',
    index_source_growth_rate_id: draft.index_source_growth_rate_id === ''
      ? null
      : Number(draft.index_source_growth_rate_id)
  }

  if (draft.profile_type === 'INDEXATION') {
    return {
      ...base,
      first_review_date: stringOrNil(draft.first_review_date),
      min_rate: numeric(draft.min_rate),
      max_rate: numeric(draft.max_rate),
      hurdle_rate: null
    }
  }
  return {
    ...base,
    first_review_date: null,
    min_rate: null,
    max_rate: null,
    hurdle_rate: numeric(draft.hurdle_rate)
  }
}

export default function IndexationProfilesTable(props) {
  const {
    valuationId,
    indexUrl,
    createUrl,
    updateUrl,
    destroyUrl,
    csrfToken,
    readOnly = false,
    translations
  } = props

  const [profiles, setProfiles] = useState([])
  const [growthRates, setGrowthRates] = useState([])
  const [loading, setLoading] = useState(true)
  const [modalState, setModalState] = useState(null) // { mode: 'create'|'edit', draft }

  const fetchProfiles = useCallback(() => {
    fetch(indexUrl, { headers: { Accept: 'application/json' } })
      .then(r => r.json())
      .then(body => {
        if (body && body.success) {
          setProfiles(body.data || [])
          setGrowthRates(body.meta?.growth_rates || [])
        }
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [indexUrl])

  useEffect(() => { fetchProfiles() }, [fetchProfiles])

  useEffect(() => {
    if (!valuationId) return
    const subscription = cable.subscriptions.create(
      { channel: 'IndexationProfilesChannel', valuation_id: valuationId },
      { received: () => { fetchProfiles() } }
    )
    return () => subscription.unsubscribe()
  }, [valuationId, fetchProfiles])

  const openCreate = useCallback(() => setModalState({ mode: 'create', draft: newDraft() }), [])

  useEffect(() => {
    const handler = () => openCreate()
    window.addEventListener('openIndexationProfileModal', handler)
    return () => window.removeEventListener('openIndexationProfileModal', handler)
  }, [openCreate])

  const openEdit = useCallback((profile) => setModalState({ mode: 'edit', draft: profileToDraft(profile) }), [])
  const closeModal = useCallback(() => setModalState(null), [])

  const handleDelete = useCallback((profile) => {
    const message = t('delete_confirm', translations, { name: profile.name })
    if (!window.confirm(message)) return
    fetch(destroyUrl, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ request_id: crypto.randomUUID(), id: profile.id })
    })
      .then(r => r.json())
      .then(body => {
        if (!body.success) {
          window.alert(t('delete_failure', translations, { error: body.error || t('unknown_error', translations) }))
        }
        // Channel push will refresh the table on success.
      })
  }, [destroyUrl, csrfToken, translations])

  const growthRateById = useMemo(() => {
    const map = new Map()
    growthRates.forEach(gr => map.set(gr.id, gr.title))
    return map
  }, [growthRates])

  return (
    <div className="indexation-profiles">
      {loading ? (
        <div className="d-flex justify-content-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('loading', translations)}</span>
          </div>
        </div>
      ) : (
        <table className="table table-sm mb-0">
          <thead>
            <tr>
              <th>{t('columns.name', translations)}</th>
              <th>{t('columns.type', translations)}</th>
              <th>{t('columns.index_source', translations)}</th>
              <th>{t('columns.frequency', translations)}</th>
              <th>{t('columns.first_review', translations)}</th>
              <th className="text-end">{t('columns.min_rate', translations)}</th>
              <th className="text-end">{t('columns.max_rate', translations)}</th>
              <th className="text-end">{t('columns.hurdle_rate', translations)}</th>
              {!readOnly && <th className="text-end">{t('columns.actions', translations)}</th>}
            </tr>
          </thead>
          <tbody>
            {profiles.length === 0 ? (
              <tr>
                <td colSpan={readOnly ? 8 : 9} className="text-center text-muted py-3">
                  {t('empty_state', translations)}
                </td>
              </tr>
            ) : profiles.map(profile => (
              <tr key={profile.id}>
                <td>{profile.name}</td>
                <td>{t(`types.${profile.profile_type}`, translations)}</td>
                <td>{growthRateById.get(profile.index_source_growth_rate_id) || '—'}</td>
                <td>{profile.profile_type === 'GERMAN_HURDLE' ? '—' : t('annual', translations)}</td>
                <td>{profile.profile_type === 'GERMAN_HURDLE' ? '—' : fmtDate(profile.first_review_date)}</td>
                <td className="text-end">{profile.profile_type === 'GERMAN_HURDLE' ? '—' : fmtRate(profile.min_rate)}</td>
                <td className="text-end">{profile.profile_type === 'GERMAN_HURDLE' ? '—' : fmtRate(profile.max_rate)}</td>
                <td className="text-end">{fmtRate(profile.hurdle_rate)}</td>
                {!readOnly && (
                  <td className="text-end">
                    <button type="button" className="btn btn-sm btn-link p-0 me-2" onClick={() => openEdit(profile)}>
                      {t('actions.edit', translations)}
                    </button>
                    <button type="button" className="btn btn-sm btn-link p-0 text-danger" onClick={() => handleDelete(profile)}>
                      {t('actions.delete', translations)}
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <IndexationProfileModal
        state={modalState}
        growthRates={growthRates}
        createUrl={createUrl}
        updateUrl={updateUrl}
        csrfToken={csrfToken}
        onClose={closeModal}
        existingNames={profiles}
        translations={translations}
      />
    </div>
  )
}

function IndexationProfileModal({ state, growthRates, createUrl, updateUrl, csrfToken, onClose, existingNames, translations }) {
  const modalRef = useRef(null)
  const bsModalRef = useRef(null)
  const [draft, setDraft] = useState(newDraft())
  const [saving, setSaving] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')

  const open = state !== null
  const mode = state?.mode

  useEffect(() => {
    if (state) {
      setDraft(state.draft)
      setErrorMsg('')
    }
  }, [state])

  useEffect(() => {
    if (!modalRef.current) return
    if (open) {
      if (!bsModalRef.current) bsModalRef.current = new Modal(modalRef.current)
      bsModalRef.current.show()
      const onHidden = () => onClose()
      modalRef.current.addEventListener('hidden.bs.modal', onHidden, { once: true })
    } else if (bsModalRef.current) {
      bsModalRef.current.hide()
    }
  }, [open, onClose])

  const setField = (field, value) => setDraft(prev => ({ ...prev, [field]: value }))

  // Swarm D6: when the user switches profile_type, blank fields that
  // belong to the OTHER type. Preserves consistency with the server-side
  // normalisation in Operations::IndexationProfiles::Create#commit! and
  // prevents stale values from re-appearing if the user toggles back.
  const changeProfileType = (next) => {
    setDraft(prev => {
      const cleared = { ...prev, profile_type: next }
      if (next === 'INDEXATION') {
        cleared.hurdle_rate = ''
      } else if (next === 'GERMAN_HURDLE') {
        cleared.first_review_date = ''
        cleared.min_rate = ''
        cleared.max_rate = ''
      }
      return cleared
    })
  }

  const validationErrors = useMemo(() => {
    const errors = {}
    if (!draft.name || draft.name.trim() === '') errors.name = t('modal.validation.name_required', translations)
    const duplicate = existingNames.find(p => p.name?.trim() === draft.name?.trim() && p.id !== draft.id)
    if (duplicate) errors.name = t('modal.validation.name_duplicate', translations)

    if (draft.profile_type === 'INDEXATION') {
      const min = draft.min_rate === '' ? null : Number(draft.min_rate)
      const max = draft.max_rate === '' ? null : Number(draft.max_rate)
      if (min !== null && max !== null && min > max) {
        errors.bounds = t('modal.validation.bounds', translations)
      }
    }
    return errors
  }, [draft, existingNames, translations])

  const canSave = !saving && Object.keys(validationErrors).length === 0

  const save = useCallback(async () => {
    if (!canSave) return
    setSaving(true)
    setErrorMsg('')
    const payload = {
      request_id: crypto.randomUUID(),
      ...draftToPayload(draft)
    }
    const url = mode === 'create' ? createUrl : updateUrl
    const method = mode === 'create' ? 'POST' : 'PATCH'
    if (mode === 'edit') payload.id = draft.id

    try {
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify(payload)
      })
      const body = await response.json()
      if (body.success) {
        onClose()
      } else {
        setErrorMsg(body.error || t('modal.save_failed', translations))
      }
    } catch (err) {
      setErrorMsg(t('modal.save_failed', translations))
    } finally {
      setSaving(false)
    }
  }, [canSave, mode, createUrl, updateUrl, csrfToken, draft, onClose, translations])

  return (
    <div ref={modalRef} className="modal fade" tabIndex={-1} aria-hidden="true">
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {mode === 'create' ? t('modal.title_create', translations) : t('modal.title_edit', translations)}
            </h5>
            <button type="button" className="btn-close" aria-label={t('modal.close', translations)} onClick={onClose} />
          </div>
          <div className="modal-body">
            {errorMsg && <div className="alert alert-danger">{errorMsg}</div>}

            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">{t('modal.labels.name', translations)}</label>
                <input
                  type="text"
                  className={`form-control${validationErrors.name ? ' is-invalid' : ''}`}
                  value={draft.name}
                  onChange={(e) => setField('name', e.target.value)}
                />
                {validationErrors.name && <div className="invalid-feedback">{validationErrors.name}</div>}
              </div>

              <div className="col-md-6">
                <label className="form-label">{t('modal.labels.type', translations)}</label>
                <select
                  className="form-select"
                  value={draft.profile_type}
                  onChange={(e) => changeProfileType(e.target.value)}
                >
                  {PROFILE_TYPE_VALUES.map(value => (
                    <option key={value} value={value}>{t(`types.${value}`, translations)}</option>
                  ))}
                </select>
              </div>

              <div className="col-md-6">
                <label className="form-label">{t('modal.labels.index_source', translations)}</label>
                <select
                  className="form-select"
                  value={draft.index_source_growth_rate_id}
                  onChange={(e) => setField('index_source_growth_rate_id', e.target.value)}
                >
                  <option value="">{t('modal.index_source_none', translations)}</option>
                  {growthRates.map(gr => (
                    <option key={gr.id} value={gr.id}>{gr.title}</option>
                  ))}
                </select>
              </div>

              <div className="col-md-6">
                <label className="form-label">{t('modal.labels.frequency', translations)}</label>
                <input type="text" className="form-control" value={t('annual', translations)} readOnly />
              </div>

              {draft.profile_type === 'INDEXATION' && (
                <>
                  <div className="col-md-4">
                    <label className="form-label">{t('modal.labels.first_review_date', translations)}</label>
                    <input
                      type="date"
                      className="form-control"
                      value={draft.first_review_date}
                      onChange={(e) => setField('first_review_date', e.target.value)}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">{t('modal.labels.min_rate', translations)}</label>
                    <input
                      type="number"
                      step="0.01"
                      className={`form-control${validationErrors.bounds ? ' is-invalid' : ''}`}
                      value={draft.min_rate}
                      onChange={(e) => setField('min_rate', e.target.value)}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">{t('modal.labels.max_rate', translations)}</label>
                    <input
                      type="number"
                      step="0.01"
                      className={`form-control${validationErrors.bounds ? ' is-invalid' : ''}`}
                      value={draft.max_rate}
                      onChange={(e) => setField('max_rate', e.target.value)}
                    />
                    {validationErrors.bounds && <div className="invalid-feedback">{validationErrors.bounds}</div>}
                  </div>
                </>
              )}

              {draft.profile_type === 'GERMAN_HURDLE' && (
                <div className="col-md-6">
                  <label className="form-label">{t('modal.labels.hurdle_rate', translations)}</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-control"
                    value={draft.hurdle_rate}
                    onChange={(e) => setField('hurdle_rate', e.target.value)}
                  />
                </div>
              )}
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>{t('modal.cancel', translations)}</button>
            <button type="button" className="btn btn-primary" onClick={save} disabled={!canSave}>
              {saving ? t('modal.saving', translations) : t('modal.save', translations)}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
